/**
 * PECE Tabs Bundle — Phase 1 of PECE closure
 *
 * Provides the 5 previously-missing workspace tabs as a single import:
 *   • RequestsTab     — PPR list with submit / approve / reject
 *   • ExecutionTab    — MIR list with issue / receive / inspect / accept
 *   • CommercialTab   — MAR list + PECE Claims
 *   • CostControlTab  — Cost Rollup + per-bucket breakdown
 *   • PHIDashboardTab — Project Health Index with 4 component bars
 *
 * Each tab is a self-contained component consuming live backend APIs.
 * Status pills, KPI cards, and action buttons share a common visual
 * language so the workspace feels unified.
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, ClipboardList, Package,
  FileText as FileTextIcon, Layers,
  ArrowRight, Truck, ClipboardCheck, AlertTriangle,
  TrendingUp, TrendingDown, DollarSign, Activity, Gauge,
} from "lucide-react";

const SAR = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "SAR", maximumFractionDigits: 0,
  }).format(Number(n || 0));

const NUM = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(n || 0));

const Pill = ({ status, palette = "default" }) => {
  const presets = {
    default: {
      draft: "bg-slate-100 text-slate-700 border-slate-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      submitted: "bg-blue-50 text-blue-700 border-blue-200",
      under_review: "bg-blue-50 text-blue-700 border-blue-200",
      approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      rejected: "bg-rose-50 text-rose-700 border-rose-200",
      cancelled: "bg-slate-100 text-slate-500 border-slate-200",
      issued: "bg-blue-50 text-blue-700 border-blue-200",
      received: "bg-cyan-50 text-cyan-700 border-cyan-200",
      inspected: "bg-violet-50 text-violet-700 border-violet-200",
      accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
      certified: "bg-emerald-50 text-emerald-700 border-emerald-200",
      qa_approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      consultant_approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
  };
  const map = presets[palette] || presets.default;
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};

const SectionTitle = ({ children, count }) => (
  <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
    {children}
    {count !== undefined && (
      <span className="ml-2 text-xs font-normal text-slate-500">({count})</span>
    )}
  </div>
);

const Loading = () => (
  <div className="flex items-center justify-center py-12 text-slate-500">
    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جاري التحميل…
  </div>
);

const EmptyRow = ({ cols, text }) => (
  <tr>
    <td colSpan={cols} className="px-4 py-8 text-center text-slate-400">{text}</td>
  </tr>
);

// ─── Requests (PPR) ───────────────────────────────────────────────

export const RequestsTab = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/pece/projects/${projectId}/ppr`);
        if (alive) setRows(r.data || []);
      } catch (e) {
        if (alive) toast.error("فشل تحميل طلبات الشراء");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, reload]);

  const act = async (id, op) => {
    setActing(`${id}-${op}`);
    try {
      await axiosInstance.post(`/pece/projects/${projectId}/ppr/${id}/${op}`);
      toast.success(op);
      setReload((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `فشل ${op}`);
    } finally {
      setActing(null);
    }
  };

  if (loading) return <Loading />;
  return (
    <div data-testid="pece-requests-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <SectionTitle count={rows.length}>طلبات الشراء (PPR)</SectionTitle>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">PPR #</th>
            <th className="px-4 py-2 text-left">Item</th>
            <th className="px-4 py-2 text-right">Qty</th>
            <th className="px-4 py-2 text-left">Required By</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody data-testid="pece-requests-rows">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`ppr-row-${r.id}`}
                className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2 font-mono">{r.ppr_number || r.id?.slice(0, 8)}</td>
              <td className="px-4 py-2">{r.item_description || r.description || "—"}</td>
              <td className="px-4 py-2 text-right">{NUM(r.quantity)} {r.unit || ""}</td>
              <td className="px-4 py-2 text-slate-500">{r.required_by || "—"}</td>
              <td className="px-4 py-2"><Pill status={r.status} /></td>
              <td className="px-4 py-2 text-right space-x-1">
                {r.status === "draft" && (
                  <button data-testid={`ppr-submit-${r.id}`}
                    disabled={acting === `${r.id}-submit`}
                    onClick={() => act(r.id, "submit")}
                    className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                    Submit
                  </button>
                )}
                {(r.status === "submitted" || r.status === "under_review") && (
                  <>
                    <button data-testid={`ppr-approve-${r.id}`}
                      disabled={acting === `${r.id}-approve`}
                      onClick={() => act(r.id, "approve")}
                      className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                      Approve
                    </button>
                    <button data-testid={`ppr-reject-${r.id}`}
                      disabled={acting === `${r.id}-reject`}
                      onClick={() => act(r.id, "reject")}
                      className="rounded-md bg-rose-600 px-2 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50">
                      Reject
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <EmptyRow cols={6} text="لا توجد طلبات شراء حالياً." />}
        </tbody>
      </table>
    </div>
  );
};

// ─── Execution (MIR) ──────────────────────────────────────────────

export const ExecutionTab = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/pece/mir?project_id=${projectId}`);
        if (alive) setRows(r.data || []);
      } catch (e) {
        if (alive) toast.error("فشل تحميل سندات الإصدار");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, reload]);

  const act = async (id, op) => {
    setActing(`${id}-${op}`);
    try {
      await axiosInstance.post(`/pece/mir/${id}/${op}`);
      toast.success(op);
      setReload((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `فشل ${op}`);
    } finally {
      setActing(null);
    }
  };

  const next = {
    draft: "issue", issued: "receive", received: "inspect", inspected: "accept",
  };
  const nextLabel = {
    issue: "Issue", receive: "Receive", inspect: "Inspect", accept: "Accept",
  };

  if (loading) return <Loading />;
  return (
    <div data-testid="pece-execution-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <SectionTitle count={rows.length}>تنفيذ سندات الإصدار (MIR)</SectionTitle>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">MIR #</th>
            <th className="px-4 py-2 text-left">Item</th>
            <th className="px-4 py-2 text-right">Qty</th>
            <th className="px-4 py-2 text-left">Supplier</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody data-testid="pece-execution-rows">
          {rows.map((r) => {
            const op = next[r.status];
            return (
              <tr key={r.id} data-testid={`mir-row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono">{r.mir_number || r.id?.slice(0, 8)}</td>
                <td className="px-4 py-2">{r.item_description || r.description || "—"}</td>
                <td className="px-4 py-2 text-right">{NUM(r.quantity)} {r.unit || ""}</td>
                <td className="px-4 py-2 text-slate-500">{r.supplier_name || "—"}</td>
                <td className="px-4 py-2"><Pill status={r.status} /></td>
                <td className="px-4 py-2 text-right">
                  {op && (
                    <button data-testid={`mir-${op}-${r.id}`}
                      disabled={acting === `${r.id}-${op}`}
                      onClick={() => act(r.id, op)}
                      className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                      {acting === `${r.id}-${op}` ? "..." : nextLabel[op]}
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && <EmptyRow cols={6} text="لا توجد سندات إصدار حالياً." />}
        </tbody>
      </table>
    </div>
  );
};

// ─── Commercial (MAR + Claims) ────────────────────────────────────

export const CommercialTab = ({ projectId }) => {
  const [mars, setMars] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const [m, c] = await Promise.all([
          axiosInstance.get(`/pece/mar?project_id=${projectId}`),
          axiosInstance.get(`/pece/claims?project_id=${projectId}`),
        ]);
        if (alive) { setMars(m.data || []); setClaims(c.data || []); }
      } catch (e) {
        if (alive) toast.error("فشل تحميل البيانات التجارية");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, reload]);

  const actMar = async (id, op) => {
    setActing(`mar-${id}-${op}`);
    try {
      await axiosInstance.post(`/pece/mar/${id}/${op}`);
      toast.success(op);
      setReload((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `فشل ${op}`);
    } finally {
      setActing(null);
    }
  };

  const actClaim = async (id, op) => {
    setActing(`claim-${id}-${op}`);
    try {
      await axiosInstance.post(`/pece/claims/${id}/${op}`);
      toast.success(op);
      setReload((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `فشل ${op}`);
    } finally {
      setActing(null);
    }
  };

  if (loading) return <Loading />;
  return (
    <div className="space-y-6">
      {/* MAR */}
      <div data-testid="pece-mar-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <SectionTitle count={mars.length}>طلبات اعتماد المواد (MAR)</SectionTitle>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">MAR #</th>
              <th className="px-4 py-2 text-left">Item</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="pece-mar-rows">
            {mars.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono">{r.mar_number || r.id?.slice(0, 8)}</td>
                <td className="px-4 py-2">{r.item_description || r.material_name || "—"}</td>
                <td className="px-4 py-2"><Pill status={r.status} /></td>
                <td className="px-4 py-2 text-right space-x-1">
                  {r.status === "draft" && (
                    <button data-testid={`mar-submit-${r.id}`}
                      onClick={() => actMar(r.id, "submit")}
                      className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white">Submit</button>
                  )}
                  {r.status === "submitted" && (
                    <button data-testid={`mar-qa-${r.id}`}
                      onClick={() => actMar(r.id, "qa-approve")}
                      className="rounded-md bg-emerald-600 px-2 py-1 text-xs text-white">QA Approve</button>
                  )}
                  {r.status === "qa_approved" && (
                    <button data-testid={`mar-consultant-${r.id}`}
                      onClick={() => actMar(r.id, "consultant-approve")}
                      className="rounded-md bg-emerald-700 px-2 py-1 text-xs text-white">Consultant Approve</button>
                  )}
                </td>
              </tr>
            ))}
            {mars.length === 0 && <EmptyRow cols={4} text="لا توجد طلبات اعتماد مواد." />}
          </tbody>
        </table>
      </div>

      {/* PECE Claims */}
      <div data-testid="pece-claims-panel" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <SectionTitle count={claims.length}>مطالبات الاستهلاك (PECE Claims)</SectionTitle>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Claim #</th>
              <th className="px-4 py-2 text-right">Value</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="pece-claims-rows">
            {claims.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono">{c.claim_number || c.id?.slice(0, 8)}</td>
                <td className="px-4 py-2 text-right font-semibold">{SAR(c.total_value || c.amount)}</td>
                <td className="px-4 py-2"><Pill status={c.status} /></td>
                <td className="px-4 py-2 text-right space-x-1">
                  {c.status === "draft" && (
                    <button data-testid={`claim-submit-${c.id}`}
                      onClick={() => actClaim(c.id, "submit")}
                      className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white">Submit</button>
                  )}
                  {c.status === "submitted" && (
                    <button data-testid={`claim-certify-${c.id}`}
                      onClick={() => actClaim(c.id, "certify")}
                      className="rounded-md bg-amber-600 px-2 py-1 text-xs text-white">Certify</button>
                  )}
                  {c.status === "certified" && (
                    <button data-testid={`claim-approve-${c.id}`}
                      onClick={() => actClaim(c.id, "approve")}
                      className="rounded-md bg-emerald-600 px-2 py-1 text-xs text-white">Approve</button>
                  )}
                </td>
              </tr>
            ))}
            {claims.length === 0 && <EmptyRow cols={4} text="لا توجد مطالبات استهلاك." />}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Cost Control ─────────────────────────────────────────────────

const CostCard = ({ label, value, sub, tone = "slate", icon: Icon, testid }) => {
  const tones = {
    slate:   "from-slate-50 to-white border-slate-200",
    blue:    "from-blue-50 to-white border-blue-200",
    amber:   "from-amber-50 to-white border-amber-200",
    green:   "from-emerald-50 to-white border-emerald-200",
    red:     "from-rose-50 to-white border-rose-200",
    indigo:  "from-indigo-50 to-white border-indigo-200",
  };
  return (
    <div data-testid={testid}
          className={`rounded-xl border bg-gradient-to-br p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

export const CostControlTab = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/pece/projects/${projectId}/cost-rollup`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error("فشل تحميل إجمالي التكلفة");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  if (loading) return <Loading />;
  if (!data) return null;

  const overBudget = data.variance < 0;
  return (
    <div data-testid="pece-cost-control-panel" className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CostCard label="Current Budget" value={SAR(data.current_budget)}
                   sub={data.approved_changes ? `+${SAR(data.approved_changes)} changes` : "no changes"}
                   tone="indigo" icon={ClipboardList} testid="cost-budget" />
        <CostCard label="Committed" value={SAR(data.committed)}
                   tone="blue" icon={Package} testid="cost-committed" />
        <CostCard label="Actual Cost" value={SAR(data.actual)}
                   sub={`Consumed ${SAR(data.consumed)}`}
                   tone="amber" icon={DollarSign} testid="cost-actual" />
        <CostCard label="Remaining" value={SAR(data.remaining)}
                   sub={data.remaining < 0 ? "OVER BUDGET" : "available"}
                   tone={data.remaining < 0 ? "red" : "green"}
                   icon={Activity} testid="cost-remaining" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <CostCard label="Forecast Final" value={SAR(data.forecast_final)}
                   tone={overBudget ? "red" : "green"}
                   icon={overBudget ? TrendingDown : TrendingUp}
                   testid="cost-forecast" />
        <CostCard label="Variance" value={SAR(data.variance)}
                   sub={`${data.variance_pct}%`}
                   tone={overBudget ? "red" : "green"} testid="cost-variance" />
        <CostCard label="Revenue Recognized" value={SAR(data.revenue_recognized)}
                   sub={`Retention ${SAR(data.retention_held)}`}
                   tone="slate" testid="cost-revenue" />
      </div>

      {/* Cost buckets */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <SectionTitle>تفصيل التكلفة حسب الحساب (GL Buckets)</SectionTitle>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Account Code</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-right">% of Actual</th>
            </tr>
          </thead>
          <tbody data-testid="cost-buckets-rows">
            {Object.entries(data.cost_buckets || {})
              .sort((a, b) => b[1] - a[1])
              .map(([code, amt]) => (
                <tr key={code} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-slate-700">{code}</td>
                  <td className="px-4 py-2 text-right font-semibold">{SAR(amt)}</td>
                  <td className="px-4 py-2 text-right text-slate-500">
                    {data.actual ? ((amt / data.actual) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
            {Object.keys(data.cost_buckets || {}).length === 0 && (
              <EmptyRow cols={3} text="لا توجد قيود تكلفة بعد." />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Project Health Index (PHI) ───────────────────────────────────

const HealthBar = ({ label, score, weight, sub, testid }) => {
  const tone = score >= 90 ? "bg-emerald-500" :
                score >= 75 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-semibold text-slate-700">{label}</div>
        <div className="text-xs text-slate-500">weight {Math.round((weight || 0) * 100)}%</div>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="text-3xl font-bold text-slate-900">{NUM(score)}</div>
        <div className="text-xs text-slate-400">/ 100</div>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full ${tone}`} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />
      </div>
      {sub && <div className="mt-2 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

export const PHIDashboardTab = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/pece/projects/${projectId}/phi`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error("فشل تحميل مؤشر صحة المشروع");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  if (loading) return <Loading />;
  if (!data) return null;
  const bandLabel = { green: "Healthy", yellow: "Watch", red: "At Risk" }[data.band];
  const bandStyle = {
    green:  "bg-emerald-50 border-emerald-200 text-emerald-700",
    yellow: "bg-amber-50  border-amber-200  text-amber-700",
    red:    "bg-rose-50   border-rose-200   text-rose-700",
  }[data.band];
  const c = data.components || {};

  return (
    <div data-testid="pece-phi-panel" className="space-y-6">
      <div className={`flex items-center justify-between rounded-xl border p-6 ${bandStyle}`}>
        <div>
          <div className="text-xs font-medium uppercase tracking-wider">Project Health Index</div>
          <div className="mt-2 text-5xl font-bold">{NUM(data.score)}</div>
          <div className="mt-1 text-sm font-medium">{bandLabel} · band <code>{data.band}</code></div>
        </div>
        <Gauge className="h-20 w-20 opacity-30" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <HealthBar label="Cost Health" score={c.cost?.score} weight={c.cost?.weight}
                    sub={`Forecast ${SAR(c.cost?.forecast_final)} vs Budget ${SAR(c.cost?.current_budget)}`}
                    testid="phi-cost" />
        <HealthBar label="Schedule Health" score={c.schedule?.score} weight={c.schedule?.weight}
                    sub={c.schedule?.spi ? `SPI ${c.schedule.spi}` : "no EVM snapshot — neutral 70"}
                    testid="phi-schedule" />
        <HealthBar label="Material Health" score={c.material?.score} weight={c.material?.weight}
                    sub={`${c.material?.accepted || 0}/${c.material?.total || 0} MIRs accepted`}
                    testid="phi-material" />
        <HealthBar label="Approval Velocity" score={c.approval?.score} weight={c.approval?.weight}
                    sub={c.approval?.median_days != null
                          ? `median ${c.approval.median_days}d across ${c.approval.samples} approvals`
                          : "no samples — neutral 70"}
                    testid="phi-approval" />
      </div>
    </div>
  );
};
